/**
 * KisiSekmesi.jsx — Kullanıcı Kartı › Kişi (backlog #379; backend #377 bağlantı, #378 okuma).
 *
 * KisiKullanicilarSekmesi'nin (Kişi Detay › Kullanıcılar, #380) AYNI bağlantısının kullanıcı tarafından görünüşü:
 * bir kullanıcı en çok BİR kişiye bağlanır (bir kişiye birden çok kullanıcı), bağlantı yalnızca "bu kullanıcı kim" kaydıdır —
 * kullanıcıyı kişinin kendi girişi yapmaz, yetki vermez. Okuma kartın `kisiBaglantilari { aktif, gecmis[] }` alanındadır;
 * yazma uçları kişi tarafınınkilerdir (`/personnel/{personId}/kullanicilar`) ve yanıtları KİŞİNİN listesidir, bu yüzden her yazmadan
 * sonra sayfa kartı yeniden okur (`onChanged`). Yazma `kisi.kullanici.manage` ister; kartın `kisiDuzenleyebilir` alanı bunu söyler.
 *
 * "Kişi Bağla": backend'de "bu kullanıcıya hangi kişi uyar" ucu yok; kişi arama listesi (`kisi-listesi?search=`, ad / soyad / T.C. / firma)
 * kullanıcının adıyla doldurulmuş açılır. İki 409 beklenir (kişi tarafıyla aynı):
 *   · UYARI_ONAYI_GEREKLI — ad soyad farklı/boş → uyarılar gösterilir, "Yine de bağla" `uyarilarOnaylandi: true` ile yeniden yollar
 *     (backend başka bir uyarı eklerse — #491 "kimlik no farklı" — aynı yoldan, uyarının kendi `mesaj`ıyla görünür)
 *   · KULLANICI_BASKA_KISIYE_BAGLI — yalnızca eşzamanlı bir bağlama kazandıysa gelebilir (kart bağsız göründüğünde) → kısa hata
 * Bağlantıyı kaldırmak kullanıcıyı silmez; satır "Geçmiş"te kalır. Kişi bağlantıları kişinin sayfasını açar ve `geri` ile bu karta döner (#273).
 */
import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Alert, Badge, Button, Card, CardBody, CardHeader, Input, ListGroup, Modal, ModalBody, ModalFooter, ModalHeader, Spinner, Table,
} from "reactstrap";
import { AlertTriangle, ChevronDown, ChevronRight, Link2, Phone, Search, Unlink } from "lucide-react";
import { useTranslation } from "react-i18next";
import i18n from "../../../../i18n";
import { baglaKisiKullanici, kaldirKisiKullanici } from "../../../../services/kisiKullaniciService";
import { getKisiListesi } from "../../../../services/kisiListService";
import { kisiSayfasiUrl } from "../../../../Components/Kisi/kisiBaglantilari";
import { firmaAdi } from "../../../../Components/Kisi/PersonelFirmaBelgeParcalari";
import { PersonelAvatar } from "../../../../Components/Kisi/PersonelKartParcalari";
import useKisiFoto from "../../../../Components/Kisi/useKisiFoto";
import { adSoyad, tarihSaat } from "../../../../Components/Kullanici/KullaniciParcalari";

const ARAMA_DEBOUNCE_MS = 350;

function baglayanSatiri(b) {
  return [b.baglayanAdi, tarihSaat(b.baglamaTarihi)].filter(Boolean).join(" · ") || "—";
}

/** Bağlı kişinin sayfası: kartta kalan kart (birleştirilmişse), Kullanıcılar sekmesi, bu karta dönüş bağlantısı. */
function kisiUrl(b, geri, geriEtiket) {
  return kisiSayfasiUrl({
    personId: b.birlestirildigiPersonId ?? b.personId, tab: "kullanicilar", geri, geriEtiket,
  });
}

function KisiFoto({ personId, fotografVar }) {
  const foto = useKisiFoto(personId, Boolean(fotografVar));
  return <PersonelAvatar photo={foto} size={56} />;
}

/** "Kişi Bağla" penceresi: kişi arama + iki 409'un işlenişi. Açıldığında bağlanır (kapanınca durumu sıfırlanır). */
function KisiBaglaModal({ kullanici, onClose, onBaglandi }) {
  const { t } = useTranslation("yonetim");
  const [q, setQ] = useState(() => (kullanici.userName || kullanici.userSurname ? adSoyad(kullanici) : ""));
  const [sonuc, setSonuc] = useState(null); // { anahtar, kayitlar, toplam, hata }
  const [isleniyor, setIsleniyor] = useState(null); // bağlanan personId
  const [hata, setHata] = useState(null);
  const [uyari, setUyari] = useState(null); // { kisi, uyarilar[] }

  const metin = q.trim();
  const aramaGecerli = metin.length >= 2;
  const yukleniyor = aramaGecerli && sonuc?.anahtar !== metin;

  // Debounce: yazarken bekler. setState zamanlayıcı geri çağrısının içinde (react-hooks/set-state-in-effect).
  useEffect(() => {
    if (!aramaGecerli) return undefined;
    let iptal = false;
    const zamanlayici = setTimeout(async () => {
      try {
        const d = await getKisiListesi({ search: metin, page: 1, size: 10 });
        if (!iptal) setSonuc({ anahtar: metin, kayitlar: d.records, toplam: d.total, hata: null });
      } catch (e) {
        if (!iptal) setSonuc({ anahtar: metin, kayitlar: [], toplam: 0, hata: e.message || i18n.t("yonetim:kisiSekmesi.kisilerAranamadi") });
      }
    }, ARAMA_DEBOUNCE_MS);
    return () => { iptal = true; clearTimeout(zamanlayici); };
  }, [metin, aramaGecerli]);

  // Yeni arama gelene dek eski sonuçlar soluk durur (yükleniyor); sayfa zıplamasın.
  const kayitlar = sonuc?.kayitlar || [];

  async function bagla(kisi, onayli = false) {
    setIsleniyor(kisi.personId);
    setHata(null);
    try {
      await baglaKisiKullanici(kisi.personId, kullanici.userId, onayli);
      onBaglandi(t("kisiSekmesi.kisiyeBaglandi", { kullanici: adSoyad(kullanici), kisi: `${kisi.name} ${kisi.surname}` }));
    } catch (e) {
      if (e.status === 409 && e.kod === "UYARI_ONAYI_GEREKLI") {
        setUyari({ kisi, uyarilar: Array.isArray(e.data?.uyarilar) ? e.data.uyarilar : [] });
      } else if (e.status === 409 && e.kod === "KULLANICI_BASKA_KISIYE_BAGLI") {
        setUyari(null);
        setHata(t("kisiSekmesi.baskaKisiyeBaglanmis", { kisi: e.data?.bagliAdSoyad ? ` (${e.data.bagliAdSoyad})` : "" }));
      } else {
        setUyari(null);
        setHata(e.message || t("kisiSekmesi.kisiBaglanamadi"));
      }
    } finally {
      setIsleniyor(null);
    }
  }

  const kapat = () => isleniyor == null && onClose();

  return (
    <Modal isOpen toggle={kapat} size="lg">
      <ModalHeader toggle={kapat}>{t("kisiSekmesi.kisiBagla")}</ModalHeader>
      <ModalBody>
        {hata && <Alert fade={false} color="danger" className="py-2 small" toggle={() => setHata(null)}>{hata}</Alert>}

        {uyari && (
          <Alert fade={false} color="warning" className="small mb-0">
            <div className="d-flex align-items-center gap-2 fw-semibold mb-1">
              <AlertTriangle size={16} />{t("kisiSekmesi.kullaniciyaAitGorunmuyor", { kisi: `${uyari.kisi.name} ${uyari.kisi.surname}` })}
            </div>
            <ul className="mb-0 ps-3">
              {uyari.uyarilar.map((u, i) => <li key={u.kod ? `${u.kod}-${i}` : i}>{u.mesaj}</li>)}
            </ul>
          </Alert>
        )}

        {!uyari && (
          <>
            <div className="position-relative mb-2">
              <Search size={14} className="position-absolute text-muted" style={{ left: 10, top: 11 }} />
              <Input
                autoFocus value={q} onChange={(e) => setQ(e.target.value)} style={{ paddingLeft: 30 }}
                placeholder={t("kisiSekmesi.aramaYeri")}
              />
              {yukleniyor && <Spinner size="sm" color="primary" className="position-absolute" style={{ right: 10, top: 10 }} />}
            </div>

            {sonuc?.hata && <div className="text-danger small">{sonuc.hata}</div>}
            {!aramaGecerli && <div className="small text-muted mt-1">{t("kisiSekmesi.enAz2Karakter")}</div>}
            {aramaGecerli && !yukleniyor && sonuc && !sonuc.hata && kayitlar.length === 0 && (
              <div className="text-muted small py-2">{t("kisiSekmesi.eslesenKisiYok")}</div>
            )}

            {aramaGecerli && kayitlar.length > 0 && (
              <ListGroup tag="div" className="border rounded" style={{ maxHeight: 360, overflowY: "auto", opacity: yukleniyor ? 0.5 : 1 }}>
                {kayitlar.map((r) => (
                  <button
                    key={r.personId} type="button"
                    className="list-group-item list-group-item-action text-start"
                    disabled={isleniyor != null} onClick={() => bagla(r)}
                  >
                    <div className="d-flex align-items-start gap-2">
                      <div className="flex-grow-1" style={{ minWidth: 0 }}>
                        <div className="fw-semibold">{r.name} {r.surname}</div>
                        <div className="small text-muted d-flex align-items-center gap-3 flex-wrap">
                          <span className="font-monospace">{r.tcNo || r.tcNoMasked || "—"}</span>
                          {r.phone && <span className="d-inline-flex align-items-center gap-1"><Phone size={11} />{r.phone}</span>}
                          {(r.firmalar || []).length > 0 && <span className="text-break">{firmaAdi(r.firmalar[0])}{r.firmalar.length > 1 ? ` +${r.firmalar.length - 1}` : ""}</span>}
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        {isleniyor === r.personId && <Spinner size="sm" color="primary" />}
                      </div>
                    </div>
                  </button>
                ))}
              </ListGroup>
            )}
            {aramaGecerli && sonuc?.toplam > kayitlar.length && (
              <div className="small text-muted mt-1">{t("kisiSekmesi.ilkSonucGosteriliyor", { count: kayitlar.length })}</div>
            )}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        {uyari && <Button color="light" onClick={() => setUyari(null)} disabled={isleniyor != null}>{t("common:geri")}</Button>}
        {uyari && (
          <Button color="warning" onClick={() => bagla(uyari.kisi, true)} disabled={isleniyor != null}>
            {isleniyor != null ? <Spinner size="sm" /> : t("kisiSekmesi.yineDeBagla")}
          </Button>
        )}
        <Button color="light" onClick={kapat} disabled={isleniyor != null}>{t("common:kapat")}</Button>
      </ModalFooter>
    </Modal>
  );
}

/**
 * @param {object}   kart       `GET /users/{id}/kart` yanıtı
 * @param {Function} onChanged  `(mesaj)` — yazma başarılı; sayfa kartı yeniden okur
 * @param {Function} onError    `(mesaj)` — kaldırma başarısız (bağla penceresi hatayı kendi içinde gösterir)
 */
export default function KisiSekmesi({ kart, onChanged, onError }) {
  const { t } = useTranslation("yonetim");
  const location = useLocation();
  const k = kart.kullanici;
  const { aktif = null, gecmis = [] } = kart.kisiBaglantilari || {};
  const duzenleyebilir = Boolean(kart.kisiDuzenleyebilir);
  const geri = `${location.pathname}${location.search}`;
  const geriEtiket = t("kisiSekmesi.kullaniciKartinaDon");

  const [baglaAcik, setBaglaAcik] = useState(false);
  const [kaldirAcik, setKaldirAcik] = useState(false);
  const [kaldiriliyor, setKaldiriliyor] = useState(false);
  const [gecmisAcik, setGecmisAcik] = useState(false);

  async function kaldir() {
    setKaldiriliyor(true);
    try {
      await kaldirKisiKullanici(aktif.personId, k.userId);
      setKaldirAcik(false);
      onChanged?.(t("kisiSekmesi.baglantiKaldirildi", { kullanici: adSoyad(k), kisi: aktif.adSoyad || t("kisiSekmesi.kisi") }));
    } catch (e) {
      setKaldirAcik(false);
      onError?.(e.message || t("kisiSekmesi.baglantiKaldirilamadi"));
    } finally {
      setKaldiriliyor(false);
    }
  }

  return (
    <Card className="mb-3">
      <CardHeader className="bg-transparent d-flex align-items-center gap-2 flex-wrap">
        <Link2 size={16} className="text-muted" />
        <span className="fw-semibold">{t("kisiSekmesi.bagliKisi")}</span>
        {duzenleyebilir && !aktif && (
          <Button color="green" size="sm" className="ms-auto" onClick={() => setBaglaAcik(true)}>
            <Link2 size={13} className="me-1" />{t("kisiSekmesi.kisiBagla")}
          </Button>
        )}
      </CardHeader>
      <CardBody>
        {!aktif && <div className="text-muted small">{t("kisiSekmesi.kisiyeBagliDegil")}</div>}

        {aktif && (
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <KisiFoto personId={aktif.personId} fotografVar={k.kisi?.fotografVar} />
            <div className="flex-grow-1" style={{ minWidth: 0 }}>
              <Link to={kisiUrl(aktif, geri, geriEtiket)} className="fw-semibold fs-5 text-decoration-none text-break">
                {aktif.adSoyad || t("kisiSekmesi.kisiNo", { id: aktif.personId })}
              </Link>
              <div className="small text-muted">{baglayanSatiri(aktif)}</div>
            </div>
            {duzenleyebilir && (
              <Button color="danger" outline size="sm" onClick={() => setKaldirAcik(true)}>
                <Unlink size={13} className="me-1" />{t("kisiSekmesi.baglantiyiKaldir")}
              </Button>
            )}
          </div>
        )}

        {gecmis.length > 0 && (
          <div className="mt-3">
            <Button color="link" size="sm" className="p-0 text-muted text-decoration-none" onClick={() => setGecmisAcik((a) => !a)}>
              {gecmisAcik ? <ChevronDown size={14} className="me-1" /> : <ChevronRight size={14} className="me-1" />}
              {t("kisiSekmesi.gecmis", { count: gecmis.length })}
            </Button>
            {gecmisAcik && (
              <div className="table-responsive mt-2">
                <Table className="table-sm align-middle mb-0 text-muted">
                  <thead className="small">
                    <tr><th>{t("kisiSekmesi.kisi")}</th><th>{t("kisiSekmesi.baglayan")}</th><th>{t("kisiSekmesi.kaldiran")}</th></tr>
                  </thead>
                  <tbody>
                    {gecmis.map((b) => (
                      <tr key={b.baglantiId ?? `${b.personId}-${b.baglamaTarihi}`}>
                        <td>
                          <Link to={kisiUrl(b, geri, geriEtiket)} className="text-body">{b.adSoyad || t("kisiSekmesi.kisiNo", { id: b.personId })}</Link>
                          {b.birlestirildigiPersonId != null && <Badge color="light" className="text-body border fw-normal ms-2">{t("kisiSekmesi.birlestirilmis")}</Badge>}
                        </td>
                        <td className="small">{baglayanSatiri(b)}</td>
                        <td className="small">{[b.kaldiranAdi, tarihSaat(b.kaldirmaTarihi)].filter(Boolean).join(" · ") || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </div>
        )}
      </CardBody>

      {baglaAcik && (
        <KisiBaglaModal
          kullanici={k} onClose={() => setBaglaAcik(false)}
          onBaglandi={(mesaj) => { setBaglaAcik(false); onChanged?.(mesaj); }}
        />
      )}

      <Modal isOpen={kaldirAcik} toggle={() => !kaldiriliyor && setKaldirAcik(false)}>
        <ModalHeader toggle={() => !kaldiriliyor && setKaldirAcik(false)}>{t("kisiSekmesi.baglantiKaldirilsinMi")}</ModalHeader>
        <ModalBody className="small">
          {aktif && (
            <p className="mb-0">{t("kisiSekmesi.kaldirmaAciklamasi", { kullanici: adSoyad(k), kisi: aktif.adSoyad })}</p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setKaldirAcik(false)} disabled={kaldiriliyor}>{t("kisiSekmesi.vazgec")}</Button>
          <Button color="danger" onClick={kaldir} disabled={kaldiriliyor}>
            {kaldiriliyor ? <Spinner size="sm" /> : t("kisiSekmesi.baglantiyiKaldir")}
          </Button>
        </ModalFooter>
      </Modal>
    </Card>
  );
}
